import { useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client/react'

import {
  APPLICATION_ASSISTANT_SUPPORT_QUERY,
  SEND_APPLICATION_ASSISTANT_SUPPORT_MUTATION,
} from './data'
import type { ChatMessage } from './types'

export const SHOW_APPLICANT_DETAILS_TOOL = 'show_applicant_details'

/** Every key a side effect could fire on: card keys and tool-row ids. The
 * pane seeds the hook with the hydrated conversation's keys so a reload
 * never reopens Intercom or renavigates. */
export const collectSideEffectKeys = (messages: ChatMessage[]): string[] =>
  messages.flatMap((message) => [
    ...message.cards.map((card) => card.key),
    ...message.toolCalls.map((row) => row.id),
  ])

const callIntercom = (...args: unknown[]) => {
  if (typeof window.Intercom !== 'function') return false
  try {
    window.Intercom(...args)
    return true
  } catch {
    return false
  }
}

/** New tickets are created by Send; Messenger only opens existing support. */
export const openSupportMessenger = (intercomConversationId?: string | null) =>
  intercomConversationId
    ? callIntercom('showConversation', intercomConversationId)
    : callIntercom('showMessages')

export const openSupportHome = () => callIntercom('show')

/** Resolve webhook-created links at the point of opening support. A no-cache
 * query cannot rehydrate the pane or overwrite an in-flight transcript. */
export const useSupportMessenger = ({
  conversationId,
  intercomConversationId,
  onSupportOpened,
}: {
  conversationId: string | null
  intercomConversationId: string | null
  onSupportOpened?: () => void
}) => {
  const client = useApolloClient()
  const [sent, setSent] = useState<{
    conversationId: string
    id: string
  } | null>(null)
  const [sending, setSending] = useState(false)
  const inFlight = useRef(false)
  const activeConversationRef = useRef(conversationId)
  activeConversationRef.current = conversationId
  const openingSupportRef = useRef<{
    conversationId: string
    promise: Promise<void>
  } | null>(null)
  useEffect(() => {
    activeConversationRef.current = conversationId
    return () => {
      activeConversationRef.current = null
    }
  }, [conversationId])

  const linkedId =
    sent?.conversationId === conversationId ? sent.id : intercomConversationId

  const openSupport = async (conversationId: string) => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      const { data } = await Promise.race([
        client.query<{
          applicationAssistantConversation: {
            id: string
            intercomConversationId: string | null
          } | null
        }>({
          query: APPLICATION_ASSISTANT_SUPPORT_QUERY,
          variables: { conversationId },
          fetchPolicy: 'no-cache',
        }),
        new Promise<{ data: null }>((resolve) => {
          timeout = setTimeout(() => resolve({ data: null }), 5000)
        }),
      ])
      if (activeConversationRef.current !== conversationId) return
      const conversation = data?.applicationAssistantConversation
      const linkedId =
        conversation?.id === conversationId
          ? conversation.intercomConversationId
          : null
      if (linkedId) setSent({ conversationId, id: linkedId })
      if (openSupportMessenger(linkedId)) onSupportOpened?.()
    } catch {
      // When the link cannot be checked, the inbox still lets the agent
      // reopen their ticket without composing a duplicate conversation.
      if (
        activeConversationRef.current === conversationId &&
        openSupportMessenger()
      )
        onSupportOpened?.()
    } finally {
      clearTimeout(timeout)
    }
  }

  const lookupSupport = (id: string) => {
    const pending = openingSupportRef.current
    if (pending?.conversationId === id) {
      return pending.promise
    }
    const promise = openSupport(id)
    openingSupportRef.current = { conversationId: id, promise }
    return promise.finally(() => {
      if (openingSupportRef.current?.promise === promise)
        openingSupportRef.current = null
    })
  }

  const showSupport = () => {
    if (linkedId || !conversationId) {
      const opened = openSupportMessenger(linkedId)
      if (opened) onSupportOpened?.()
      return
    }
    return lookupSupport(conversationId)
  }

  const sendSupport = async (supportKey: string, draft: string) => {
    if (!conversationId || inFlight.current) return
    inFlight.current = true
    setSending(true)
    try {
      const { data } = await client.mutate<{
        sendApplicationAssistantSupport: string
      }>({
        mutation: SEND_APPLICATION_ASSISTANT_SUPPORT_MUTATION,
        variables: { conversationId, supportKey, draft },
      })
      const id = data?.sendApplicationAssistantSupport
      if (!id)
        throw new Error(
          'Support did not confirm your request. Please try again.'
        )
      if (activeConversationRef.current !== conversationId) return
      setSent({ conversationId, id })
      if (!openSupportMessenger(id)) {
        throw new Error(
          'Your message was sent. Open support to view the conversation.'
        )
      }
      onSupportOpened?.()
    } finally {
      inFlight.current = false
      setSending(false)
    }
  }
  return {
    openSupport: showSupport,
    openSupportHistory: showSupport,
    sendSupport,
    intercomConversationId: linkedId,
    sending,
  }
}

export const useAssistantSideEffects = ({
  messages,
  onNavigateApplicant,
  onOpenSupport,
  hydratedKeys,
  hasSupportConversation = false,
  applicationToOpen,
  onApplicationBound,
}: {
  messages: ChatMessage[]
  onNavigateApplicant: (applicantId: number) => void
  onOpenSupport: () => void | Promise<void>
  hydratedKeys: readonly string[]
  hasSupportConversation?: boolean
  applicationToOpen?: { applicationId: number; bound: boolean } | null
  onApplicationBound?: (applicationId: number, bound: boolean) => void
}) => {
  const firedRef = useRef<Set<string> | null>(null)
  if (firedRef.current === null) firedRef.current = new Set(hydratedKeys)
  const pendingSupportRef = useRef<Promise<void> | null>(null)
  const openedApplicationRef = useRef<typeof applicationToOpen>(null)

  const navigateRef = useRef(onNavigateApplicant)
  const openSupportRef = useRef(onOpenSupport)
  const applicationBoundRef = useRef(onApplicationBound)
  useEffect(() => {
    navigateRef.current = onNavigateApplicant
    openSupportRef.current = onOpenSupport
    applicationBoundRef.current = onApplicationBound
  })

  useEffect(() => {
    const fired = firedRef.current
    if (!fired) return
    const last = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant')
    if (!last) return

    for (const card of last.cards) {
      if (card.type !== 'openSupport' || fired.has(card.key)) continue
      fired.add(card.key)
      if (!hasSupportConversation) continue
      pendingSupportRef.current = Promise.all([
        pendingSupportRef.current,
        openSupportRef.current(),
      ]).then(() => undefined)
    }

    if (!last.done) return
    let active = true
    const navigate = async () => {
      // Navigation unmounts the pane and cancels an unfinished ticket lookup.
      if (pendingSupportRef.current) await pendingSupportRef.current
      if (!active) return
      for (const row of last.toolCalls) {
        if (
          row.name !== SHOW_APPLICANT_DETAILS_TOOL ||
          row.status !== 'done' ||
          fired.has(row.id)
        ) {
          continue
        }
        fired.add(row.id)
        if (typeof row.applicantId === 'number') {
          navigateRef.current(row.applicantId)
        }
      }
      if (
        applicationToOpen &&
        openedApplicationRef.current !== applicationToOpen
      ) {
        openedApplicationRef.current = applicationToOpen
        applicationBoundRef.current?.(
          applicationToOpen.applicationId,
          applicationToOpen.bound
        )
      }
    }
    void navigate()
    return () => {
      active = false
    }
  }, [messages, applicationToOpen, hasSupportConversation])
}
